"use client";

import NextImage from "next/image";
import NextLink from "next/link";
import { Button as ReactAriaButton } from "react-aria-components";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";

import { Avatar } from "@jetblue/kit/avatar";
import { Popover } from "@jetblue/kit/popover";

import { Ad, type AdProperties } from "../ad";
import {
  AuthenticatedCallToActions,
  type AuthenticatedCallToActionsProperties,
} from "./call-to-actions";
import {
  CreditProfileUser,
  type CreditProfileUserProperties,
} from "./credit-profile-user";
import { TrueBlueUser, type TrueBlueUserProperties } from "./true-blue-user";

import { constructReturnURL } from "../../../../../helpers/links/return-url";
import type { EnhancedUserData } from "../../../../../helpers/user-data/enhance-user-data";

export type AuthenticatedProperties = {
  creditProfileMenu: {
    ad: AdProperties;
    cta: CreditProfileUserProperties["cta"];
    callToActions: AuthenticatedCallToActionsProperties;
    labels: CreditProfileUserProperties["labels"];
  };
  customerDetails: EnhancedUserData;
  isCreditProfile?: boolean | undefined;
  loggedInMenu: {
    ad: AdProperties;
    callToActions: AuthenticatedCallToActionsProperties;
    image?: string;
    imageAlt?: string;
    imageSrc?: unknown;
    loyalty: TrueBlueUserProperties["loyalty"];
    signOut: { href?: string; label?: string; target?: "_blank" | "_self" };
  };
};
export const Authenticated = ({
  creditProfileMenu,
  customerDetails,
  isCreditProfile,
  loggedInMenu,
}: AuthenticatedProperties) => {
  const { firstName, fullName, initials, isMosaic, userImageSrc } =
    customerDetails;

  const { ad: creditProfileAd, callToActions: creditProfileCtas } =
    creditProfileMenu;
  const {
    ad: loyaltyAd,
    callToActions: loyaltyCtas,
    loyalty,
    signOut,
  } = loggedInMenu;

  const adData = isCreditProfile ? creditProfileAd : loyaltyAd;
  const callToActionData = isCreditProfile ? creditProfileCtas : loyaltyCtas;

  const signoutHref = signOut.href ?? "/logout";
  const pathname = usePathname();
  const [href, setHref] = useState(constructReturnURL(signoutHref));

  useEffect(() => {
    setHref(constructReturnURL(signoutHref));
  }, [signoutHref, pathname]);

  return (
    <Popover
      className="
        size-full overflow-scroll rounded-none border-none bg-white p-0

        md:h-unset md:w-[375px] md:rounded-b-md md:shadow-jb-2
      "
      offset={0}
      placement="bottom right"
      showOverlayArrow={false}
      triggerButton={
        <ReactAriaButton
          aria-haspopup="dialog"
          className="
            relative flex h-full shrink-0 grow-0 cursor-pointer items-center
            justify-center gap-2

            after:absolute after:bottom-0 after:h-1 after:w-0 after:bg-orange
            after:transition-all after:content-['_']

            hover:after:w-full

            focus-visible:outline-1 focus-visible:outline-offset-[3px]
            focus-visible:outline-white

            aria-expanded:after:w-full
          "
          data-fs-element={
            isCreditProfile
              ? "main-nav-menu-button-user-credit-profile"
              : "main-nav-menu-button-user-trueblue"
          }
        >
          <Avatar
            as={NextImage}
            initials={initials}
            isMosaic={isMosaic}
            name={fullName}
            size="small"
            source={userImageSrc}
          />

          {!isCreditProfile && (
            <span
              className="
                hidden text-s-body-extra-bold text-white

                lg:inline-block
              "
            >
              {customerDetails.loyaltyDetails.accountBalance?.toLocaleString(
                "en",
              ) ?? 0}
              &nbsp;{loyalty.labels.pts}
            </span>
          )}
        </ReactAriaButton>
      }
    >
      {isCreditProfile ? (
        <CreditProfileUser
          cta={creditProfileMenu.cta}
          firstName={firstName}
          labels={creditProfileMenu.labels}
          loyalty={loyalty}
          travelBankDetails={customerDetails.travelBankDetails}
        />
      ) : (
        <TrueBlueUser
          customerDetails={customerDetails}
          isMosaic={isMosaic}
          loyalty={loyalty}
        />
      )}

      <AuthenticatedCallToActions
        {...callToActionData}
        dataAttributePrefix={
          isCreditProfile ? "user-credit-profile" : "user-trueblue"
        }
      />

      {href && signOut.label && (
        <section className="px-6 py-4">
          <NextLink
            className="
              block text-royal-blue

              hover:text-core-blue hover:underline

              focus-visible:focus-outline
            "
            data-fs-element={
              isCreditProfile
                ? "user-credit-profile-sign-out-link"
                : "user-trueblue-sign-out-link"
            }
            href={href}
            target={signOut.target}
          >
            {signOut.label}
          </NextLink>
        </section>
      )}

      <Ad
        {...adData}
        dataAttributePrefix={
          isCreditProfile ? "user-credit-profile" : "user-trueblue"
        }
      />
    </Popover>
  );
};
